import "@/App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
// Public core (SEO-critical) tetap eager — initial bundle publik ramping.
import Home from "./pages/Home";
// Catalog & ProductDetail di-lazy-kan (mengikuti 71 route lain) — hemat ~59KB source dari
// initial bundle homepage. SEO crawler tetap terlindungi prerender statis.
const Catalog = lazy(() => import("./pages/Catalog"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
import Categories from "./pages/Categories";
import NotFound from "./pages/NotFound";
// Public non-core: lazy chunk.
const Articles = lazy(() => import("./pages/Articles"));
const ArticleDetail = lazy(() => import("./pages/ArticleDetail"));
const CategoryLanding = lazy(() => import("./pages/CategoryLanding"));
const SeoLanding = lazy(() => import("./pages/SeoLanding"));
const SeoLandingPreview = lazy(() => import("./pages/SeoLandingPreview"));
const SellerProfile = lazy(() => import("./pages/SellerProfile"));
const BecomeSeller = lazy(() => import("./pages/BecomeSeller"));
const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const Cart = lazy(() => import("./pages/Cart"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const PaymentFailed = lazy(() => import("./pages/PaymentFailed"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
// Buyer dashboard group: lazy (tidak ikut initial public bundle).
const BuyerLayout = lazy(() => import("./pages/buyer/BuyerLayout"));
const BuyerDashboard = lazy(() => import("./pages/buyer/BuyerDashboard"));
const BuyerOrders = lazy(() => import("./pages/buyer/BuyerOrders"));
const BuyerOrderDetail = lazy(() => import("./pages/buyer/BuyerOrderDetail"));
const BuyerLibrary = lazy(() => import("./pages/buyer/BuyerLibrary"));
const BuyerWishlist = lazy(() => import("./pages/buyer/BuyerWishlist"));
const BuyerProfile = lazy(() => import("./pages/buyer/BuyerProfile"));
const BuyerSettings = lazy(() => import("./pages/buyer/BuyerSettings"));
const BuyerVouchers = lazy(() => import("./pages/buyer/BuyerVouchers"));
// Seller group: lazy (termasuk recharts dashboard & ImageCropper).
const SellerLayout = lazy(() => import("./pages/seller/SellerLayout"));
const SellerDashboard = lazy(() => import("./pages/seller/SellerDashboard"));
const SellerProducts = lazy(() => import("./pages/seller/SellerProducts"));
const SellerProductForm = lazy(() => import("./pages/seller/SellerProductForm"));
const SellerOrders = lazy(() => import("./pages/seller/SellerOrders"));
const SellerSales = lazy(() => import("./pages/seller/SellerSales"));
const SellerBalance = lazy(() => import("./pages/seller/SellerBalance"));
const SellerStoreProfile = lazy(() => import("./pages/seller/SellerStoreProfile"));
const SellerSettings = lazy(() => import("./pages/seller/SellerSettings"));
const SellerQuestions = lazy(() => import("./pages/seller/SellerQuestions"));
const SellerReviews = lazy(() => import("./pages/seller/SellerReviews"));
const SellerVerification = lazy(() => import("./pages/seller/SellerVerification"));

// Admin group: lazy (CMS editor, review, dsb tidak ikut initial public bundle).
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminVouchers = lazy(() => import("./pages/admin/AdminVouchers"));
const AdminVoucherForm = lazy(() => import("./pages/admin/AdminVoucherForm"));
const AdminVoucherDetail = lazy(() => import("./pages/admin/AdminVoucherDetail"));
const AdminSellers = lazy(() => import("./pages/admin/AdminSellers"));
const AdminProducts = lazy(() => import("./pages/admin/AdminProducts"));
const AdminCategories = lazy(() => import("./pages/admin/AdminCategories"));
const AdminTransactions = lazy(() => import("./pages/admin/AdminTransactions"));
const AdminProductReview = lazy(() => import("./pages/admin/AdminProductReview"));
const AdminWithdrawals = lazy(() => import("./pages/admin/AdminWithdrawals"));
const AdminRefunds = lazy(() => import("./pages/admin/AdminRefunds"));
const AdminReports = lazy(() => import("./pages/admin/AdminReports"));
const AdminReviews = lazy(() => import("./pages/admin/AdminReviews"));
const AdminVerifications = lazy(() => import("./pages/admin/AdminVerifications"));
const AdminAdmins = lazy(() => import("./pages/admin/AdminAdmins"));
const AdminAuditLog = lazy(() => import("./pages/admin/AdminAuditLog"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminArticles = lazy(() => import("./pages/admin/AdminArticles"));
const AdminArticleForm = lazy(() => import("./pages/admin/AdminArticleForm"));
const AdminSeoLandings = lazy(() => import("./pages/admin/AdminSeoLandings"));
const AdminSeoLandingForm = lazy(() => import("./pages/admin/AdminSeoLandingForm"));
const AdminContactInbox = lazy(() => import("./pages/admin/AdminContactInbox"));
// Public info/footer pages: lazy.
const About = lazy(() => import("./pages/About"));
const HelpCenter = lazy(() => import("./pages/HelpCenter"));
const HowToBuy = lazy(() => import("./pages/HowToBuy"));
const HowToSell = lazy(() => import("./pages/HowToSell"));
const Contact = lazy(() => import("./pages/Contact"));
const Terms = lazy(() => import("./pages/Terms"));
const Privacy = lazy(() => import("./pages/Privacy"));
const RefundPolicy = lazy(() => import("./pages/RefundPolicy"));
const DigitalLicense = lazy(() => import("./pages/DigitalLicense"));
const SellerGuide = lazy(() => import("./pages/SellerGuide"));
const CommissionInfo = lazy(() => import("./pages/CommissionInfo"));

function Protected({ roles, children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50" data-testid="auth-loading">
        <div className="w-8 h-8 border-2 border-brand-red border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/masuk" state={{ from: location }} replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function GuestOnly({ children }) {
  const { user, loading, dashboardPath } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (user) {
    const redirect = new URLSearchParams(location.search).get('redirect');
    return <Navigate to={redirect || dashboardPath()} replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Suspense fallback={<div className="min-h-[60vh] flex items-center justify-center" data-testid="route-loading"><div className="w-8 h-8 border-2 border-zinc-200 border-t-brand-black rounded-full animate-spin" /></div>}>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/produk" element={<Catalog />} />
      <Route path="/produk/:slug" element={<ProductDetail />} />
      <Route path="/kategori" element={<Categories />} />
      <Route path="/kategori/:slug" element={<CategoryLanding />} />
      <Route path="/artikel" element={<Articles />} />
      <Route path="/artikel/:slug" element={<ArticleDetail />} />
      <Route path="/seller/:sellerId" element={<SellerProfile />} />
      <Route path="/jadi-seller" element={<BecomeSeller />} />
      <Route path="/tentang" element={<About />} />
      <Route path="/pusat-bantuan" element={<HelpCenter />} />
      <Route path="/cara-berbelanja" element={<HowToBuy />} />
      <Route path="/cara-berjualan" element={<HowToSell />} />
      <Route path="/hubungi-kami" element={<Contact />} />
      <Route path="/syarat-ketentuan" element={<Terms />} />
      <Route path="/kebijakan-privasi" element={<Privacy />} />
      <Route path="/kebijakan-refund" element={<RefundPolicy />} />
      <Route path="/lisensi-produk-digital" element={<DigitalLicense />} />
      <Route path="/panduan-seller" element={<SellerGuide />} />
      <Route path="/komisi-pembayaran" element={<CommissionInfo />} />
      <Route path="/keranjang" element={<Cart />} />
      <Route path="/pembayaran/sukses" element={<PaymentSuccess />} />
      <Route path="/pembayaran/gagal" element={<PaymentFailed />} />
      <Route path="/masuk" element={<GuestOnly><Login /></GuestOnly>} />
      <Route path="/daftar" element={<GuestOnly><Register /></GuestOnly>} />
      <Route path="/lupa-password" element={<GuestOnly><ForgotPassword /></GuestOnly>} />

      <Route path="/dashboard" element={<Protected><BuyerLayout /></Protected>}>
        <Route index element={<BuyerDashboard />} />
        <Route path="pesanan" element={<BuyerOrders />} />
        <Route path="pesanan/:orderId" element={<BuyerOrderDetail />} />
        <Route path="produk" element={<BuyerLibrary />} />
        <Route path="wishlist" element={<BuyerWishlist />} />
        <Route path="notifikasi" element={<NotificationsPage />} />
        <Route path="profil" element={<BuyerProfile />} />
        <Route path="pengaturan" element={<BuyerSettings />} />
        <Route path="voucher" element={<BuyerVouchers />} />
      </Route>

      <Route path="/seller/dashboard" element={<Protected roles={['seller']}><SellerLayout /></Protected>}>
        <Route index element={<SellerDashboard />} />
        <Route path="produk" element={<SellerProducts />} />
        <Route path="produk/baru" element={<SellerProductForm />} />
        <Route path="produk/:productId/edit" element={<SellerProductForm />} />
        <Route path="pesanan" element={<SellerOrders />} />
        <Route path="penjualan" element={<SellerSales />} />
        <Route path="saldo" element={<SellerBalance />} />
        <Route path="pertanyaan" element={<SellerQuestions />} />
        <Route path="ulasan" element={<SellerReviews />} />
        <Route path="verifikasi" element={<SellerVerification />} />
        <Route path="notifikasi" element={<NotificationsPage />} />
        <Route path="profil-toko" element={<SellerStoreProfile />} />
        <Route path="pengaturan" element={<SellerSettings />} />
      </Route>

      <Route path="/admin" element={<Protected roles={['admin']}><AdminLayout /></Protected>}>
        <Route index element={<AdminDashboard />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="voucher" element={<AdminVouchers />} />
        <Route path="voucher/baru" element={<AdminVoucherForm />} />
        <Route path="voucher/:voucherId" element={<AdminVoucherDetail />} />
        <Route path="voucher/:voucherId/edit" element={<AdminVoucherForm />} />
        <Route path="sellers" element={<AdminSellers />} />
        <Route path="produk" element={<AdminProducts />} />
        <Route path="produk/:productId" element={<AdminProductReview />} />
        <Route path="kategori" element={<AdminCategories />} />
        <Route path="transaksi" element={<AdminTransactions />} />
        <Route path="notifikasi" element={<NotificationsPage />} />
        <Route path="withdraw" element={<AdminWithdrawals />} />
        <Route path="refund" element={<AdminRefunds />} />
        <Route path="laporan" element={<AdminReports />} />
        <Route path="ulasan" element={<AdminReviews />} />
        <Route path="verifikasi" element={<AdminVerifications />} />
        <Route path="admins" element={<AdminAdmins />} />
        <Route path="audit-log" element={<AdminAuditLog />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="artikel" element={<AdminArticles />} />
        <Route path="artikel/baru" element={<AdminArticleForm />} />
        <Route path="artikel/:articleId/edit" element={<AdminArticleForm />} />
        <Route path="seo-landing" element={<AdminSeoLandings />} />
        <Route path="seo-landing/baru" element={<AdminSeoLandingForm />} />
        <Route path="seo-landing/:landingId/edit" element={<AdminSeoLandingForm />} />
        <Route path="pesan-masuk" element={<AdminContactInbox />} />
      </Route>

      {/* Preview draft admin (content.manage, di-enforce backend) — render komponen public
          yang sama, di luar AdminLayout agar tampilan persis seperti halaman visitor. */}
      <Route path="/admin/seo-landing/preview" element={<SeoLandingPreview />} />

      {/* SEO Landing Pages: root-level /{slug} — setelah SEMUA route static di atas
          (React Router memprioritaskan static match), sebelum catch-all NotFound. */}
      <Route path="/:slug" element={<SeoLanding />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
}

// Scroll restoration global: navigasi internal (PUSH/REPLACE) selalu mulai dari atas.
// Anchor/hash (#section) tidak diganggu; Back/Forward (POP) dibiarkan ke browser.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    if (hash) return;
    if (navType === 'POP') return;
    window.scrollTo(0, 0);
  }, [pathname, hash, navType]);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <AppRouter />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
